// 8-direction A* pathfinding on the world's tile grid + line-of-sight
// path smoothing. Used by guards.ts to navigate around walls instead of
// the previous naive "bump on collision" heuristic.

import { raycastHit } from './geometry.js';
import type { Vec2, World } from '../world/types.js';

const SQRT2 = Math.SQRT2;

// dx, dy, step cost — cardinal cost 1, diagonal cost √2 (admissible for
// the octile heuristic).
const NEIGHBOURS: ReadonlyArray<readonly [number, number, number]> = [
  [-1, 0, 1],
  [1, 0, 1],
  [0, -1, 1],
  [0, 1, 1],
  [-1, -1, SQRT2],
  [1, -1, SQRT2],
  [-1, 1, SQRT2],
  [1, 1, SQRT2],
];

export interface TileCoord {
  tx: number;
  ty: number;
}

export function worldToTile(world: World, p: Vec2): TileCoord {
  return {
    tx: Math.floor(p.x / world.TILE),
    ty: Math.floor(p.y / world.TILE),
  };
}

export function tileToWorld(world: World, t: TileCoord): Vec2 {
  return {
    x: t.tx * world.TILE + world.TILE / 2,
    y: t.ty * world.TILE + world.TILE / 2,
  };
}

export function isWalkable(world: World, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= world.MW || ty >= world.MH) return false;
  return world.MAP[ty]![tx] !== '#';
}

interface AStarNode {
  tx: number;
  ty: number;
  g: number;
  h: number;
  f: number;
  parent: AStarNode | null;
}

// Octile heuristic — admissible for 8-way movement with √2 diagonal cost.
function octile(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
}

/**
 * Returns waypoints in world coordinates (tile centers) from `from` to
 * `to` inclusive, or null if no walkable path exists. Diagonals are
 * allowed but cannot cut through the corner of an adjacent wall.
 *
 * Tuned for ≤ ~500 tile grids; a Map+linear-scan open set keeps the
 * implementation small. For larger maps swap in a binary heap.
 */
export function aStar(world: World, from: Vec2, to: Vec2): Vec2[] | null {
  const start = worldToTile(world, from);
  const goal = worldToTile(world, to);

  if (!isWalkable(world, goal.tx, goal.ty)) return null;
  if (start.tx === goal.tx && start.ty === goal.ty) {
    return [tileToWorld(world, goal)];
  }

  const open = new Map<string, AStarNode>();
  const closed = new Set<string>();
  const key = (x: number, y: number): string => `${x},${y}`;

  const startNode: AStarNode = {
    tx: start.tx,
    ty: start.ty,
    g: 0,
    h: octile(start.tx, start.ty, goal.tx, goal.ty),
    f: 0,
    parent: null,
  };
  startNode.f = startNode.h;
  open.set(key(start.tx, start.ty), startNode);

  const MAX_ITER = world.MW * world.MH * 4;
  let iter = 0;

  while (open.size > 0 && iter++ < MAX_ITER) {
    // Pop lowest-f node (linear scan over the open map).
    let cur: AStarNode | null = null;
    let curKey = '';
    for (const [k, n] of open) {
      if (!cur || n.f < cur.f || (n.f === cur.f && n.h < cur.h)) {
        cur = n;
        curKey = k;
      }
    }
    if (!cur) break;

    if (cur.tx === goal.tx && cur.ty === goal.ty) {
      const path: Vec2[] = [];
      let n: AStarNode | null = cur;
      while (n) {
        path.push(tileToWorld(world, n));
        n = n.parent;
      }
      return path.reverse();
    }

    open.delete(curKey);
    closed.add(curKey);

    for (const [dx, dy, cost] of NEIGHBOURS) {
      const nx = cur.tx + dx;
      const ny = cur.ty + dy;
      const nKey = key(nx, ny);
      if (closed.has(nKey)) continue;
      if (!isWalkable(world, nx, ny)) continue;
      // Disallow diagonal moves that would cut through a wall corner.
      if (dx !== 0 && dy !== 0) {
        if (!isWalkable(world, cur.tx + dx, cur.ty)) continue;
        if (!isWalkable(world, cur.tx, cur.ty + dy)) continue;
      }
      const tentG = cur.g + cost;
      const existing = open.get(nKey);
      if (existing && tentG >= existing.g) continue;

      const node: AStarNode = {
        tx: nx,
        ty: ny,
        g: tentG,
        h: octile(nx, ny, goal.tx, goal.ty),
        f: 0,
        parent: cur,
      };
      node.f = node.g + node.h;
      open.set(nKey, node);
    }
  }

  return null;
}

/**
 * Greedy line-of-sight smoothing. From each anchor, look as far down the
 * path as possible and skip every intermediate waypoint visible from the
 * anchor (no wall between). Removes the staircase artefacts of grid-step
 * A* paths.
 */
export function smoothPath(world: World, path: ReadonlyArray<Vec2>): Vec2[] {
  if (path.length <= 2) return path.slice();

  const out: Vec2[] = [path[0]!];
  let i = 0;
  while (i < path.length - 1) {
    let j = path.length - 1;
    while (j > i + 1) {
      if (!raycastHit(world, path[i]!.x, path[i]!.y, path[j]!.x, path[j]!.y)) {
        break;
      }
      j--;
    }
    out.push(path[j]!);
    i = j;
  }
  return out;
}
